import React from "react"
import LabeledHRange from "../../../components/Inputs/Labeled/LabeledHRange/LabeledHRange"
import classes from "./DSPPanel.module.css"
import CsoundObj from "@kunstmusik/csound"
import NoteButton from "../../../components/Inputs/Button/NoteButton/NoteButton"
import RegularButton from "../../../components/Inputs/Button/RegularButton/RegularButton"

const CARRIER_AMPLITUDE = "CARRIER_AMPLITUDE"
const CARRIER_AMPLITUDE_DEFAULT = 0.2
const CARRIER_FREQUENCY = "CARRIER_FREQUENCY"
const CARRIER_FREQUENCY_DEFAULT = 0
const PANNING = "PANNING"
const PANNING_DEFAULT = 0.5
const MODULATOR_AMPLITUDE = "MODULATOR_AMPLITUDE"
const MODULATOR_AMPLITUDE_DEFAULT = 0
const MODULATOR_FREQUENCY = "MODULATOR_FREQUENCY"
const MODULATOR_FREQUENCY_DEFAULT = 0.25
const AM_AMOUNT = "AM_AMOUNT"
const AM_AMOUNT_DEFAULT = 0
const AM_FREQUENCY = "AM_FREQUENCY"
const AM_FREQUENCY_DEFAULT = 0
const BIT_REDUCER = "BIT_REDUCER"
const BIT_REDUCER_DEFAULT = 1
const MAX_FREQ_LOG = Math.log(3520)
const MIN_FREQ_LOG = Math.log(110)
const MAX_MIN_FREQ_LOG_DIFF = MAX_FREQ_LOG - MIN_FREQ_LOG

const orc = `
    sr = 48000
    ksmps = 128
    0dbfs = 1
    nchnls = 2
    
    giCarrierFrequencyLow = ${MIN_FREQ_LOG}
    giCarrierFrequencyHigh = ${MAX_FREQ_LOG}
    giAMFrequencyLow = log:i(0.2)
    giAMFrequencyHigh = log:i(3520)

    instr 1
        kCarrierAmplitude = pow:k(port:k(chnget:k("${CARRIER_AMPLITUDE}"), 0.05,\
            ${CARRIER_AMPLITUDE_DEFAULT}), 2)
        kCarrierFrequency = port:k(chnget:k("${CARRIER_FREQUENCY}"), 0.01,\
            ${CARRIER_FREQUENCY_DEFAULT})
        kCarrierFrequency = exp:k(kCarrierFrequency * (giCarrierFrequencyHigh - giCarrierFrequencyLow)\
            + giCarrierFrequencyLow)
        kModulatorAmplitude = port:k(chnget:k("${MODULATOR_AMPLITUDE}"), 0.01,\
            ${MODULATOR_AMPLITUDE_DEFAULT})
        kModulatorFrequency = port:k(chnget:k("${MODULATOR_FREQUENCY}"), 0.01,\
            ${MODULATOR_FREQUENCY_DEFAULT})
        kAMAmount = port:k(chnget:k("${AM_AMOUNT}"), 0.01, ${AM_AMOUNT_DEFAULT})
        kAMFrequency = port:k(chnget:k("${AM_FREQUENCY}"), 0.01, ${AM_FREQUENCY_DEFAULT})
        kAMFrequency = exp:k(kAMFrequency * (giAMFrequencyHigh - giAMFrequencyLow)\
            + giAMFrequencyLow)
        kModulatorFrequency = kCarrierFrequency * kModulatorFrequency
        kBits = pow:k(2, port:k(chnget:k("${BIT_REDUCER}"), 0.01, ${BIT_REDUCER_DEFAULT}) * 16)

        aModulator = poscil:a(kModulatorFrequency * kModulatorAmplitude, kModulatorFrequency)

        aSig = poscil:a(kCarrierAmplitude, kCarrierFrequency + aModulator)

        aSig *= (1 + poscil:a(kAMAmount, kAMFrequency, -1, 0.25)) * 0.5

        aSig = int:a(aSig * kBits) / kBits

        aSig *= linseg:k(0, 0.05, 1, 1, 1)
        aSig1, aSig2 pan2 aSig, port:k(chnget:k("${PANNING}"), 0.01, ${PANNING_DEFAULT})
        outch(1, aSig1, 2, aSig2)
    endin
    `

type Props = {
    csound: CsoundObj
}

const DSPPanel: React.FC<Props> = (props) => {
    const [carrierAmplitude, setAmplitudeValue] = React.useState(CARRIER_AMPLITUDE_DEFAULT)
    const [carrierFrequency, setCarrierFrequency] = React.useState(CARRIER_FREQUENCY_DEFAULT)
    const [panning, setPanning] = React.useState(PANNING_DEFAULT)
    const [modulatorAmplitude, setModulatorAmplitude] = React.useState(
        MODULATOR_AMPLITUDE_DEFAULT
    )
    const [modulatorFrequency, setModulatorFrequency] = React.useState(
        MODULATOR_FREQUENCY_DEFAULT
    )
    const [amplitudeModulationAmount, setAmplitudeModulationAmount] = React.useState(
        AM_AMOUNT_DEFAULT
    )
    const [amplitudeModulationFrequency, setAmplitudeModulationFrequency] = React.useState(
        AM_FREQUENCY_DEFAULT
    )
    const [bitReducerAmount, setBitReducerAmount] = React.useState(BIT_REDUCER_DEFAULT)

    const amplitudeChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        const val = +event.currentTarget.value
        setAmplitudeValue(val)
        props.csound.setControlChannel(CARRIER_AMPLITUDE, val)
    }

    const carrierFrequencyChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        const val = +event.currentTarget.value
        setCarrierFrequency(val)
        props.csound.setControlChannel(CARRIER_FREQUENCY, val)
    }

    const panningChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        const val = +event.currentTarget.value
        setPanning(val)
        props.csound.setControlChannel(PANNING, val)
    }

    const modulatorAmplitudeChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        const val = +event.currentTarget.value
        setModulatorAmplitude(val)
        props.csound.setControlChannel(MODULATOR_AMPLITUDE, val)
    }

    const modulatorFrequencyChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        const val = +event.currentTarget.value
        setModulatorFrequency(val)
        props.csound.setControlChannel(MODULATOR_FREQUENCY, val)
    }

    const amplitudeModulationAmountChangeHandler = (
        event: React.FormEvent<HTMLInputElement>
    ) => {
        const val = +event.currentTarget.value
        setAmplitudeModulationAmount(val)
        props.csound.setControlChannel(AM_AMOUNT, val)
    }

    const amplitudeModulationFrequencyChangeHandler = (
        event: React.FormEvent<HTMLInputElement>
    ) => {
        const val = +event.currentTarget.value
        setAmplitudeModulationFrequency(val)
        props.csound.setControlChannel(AM_FREQUENCY, val)
    }

    const bitReducerChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        const val = +event.currentTarget.value
        setBitReducerAmount(val)
        props.csound.setControlChannel(BIT_REDUCER, val)
    }

    const noteMouseEnterHandler = (frequency: number) => {
        const val = (Math.log(frequency) - MIN_FREQ_LOG) / MAX_MIN_FREQ_LOG_DIFF
        setCarrierFrequency(val)
        props.csound.setControlChannel(CARRIER_FREQUENCY, val)
    }

    const modulatorFrequencyButtonClickHandler = (ratio: number) => {
        setModulatorFrequency(ratio)
        props.csound.setControlChannel(MODULATOR_FREQUENCY, ratio)
    }

    const startCsound = () => {
        console.log("DSP PANEL MOUNTING...")
        props.csound.audioContext.resume().then(() => {
            props.csound.compileOrc(orc)
            props.csound.start()
            props.csound.setMessageCallback((message) => {})
            props.csound.setControlChannel(CARRIER_AMPLITUDE, carrierAmplitude)
            props.csound.setControlChannel(CARRIER_FREQUENCY, carrierFrequency)
            props.csound.setControlChannel(PANNING, panning)
            props.csound.setControlChannel(MODULATOR_AMPLITUDE, modulatorAmplitude)
            props.csound.setControlChannel(MODULATOR_FREQUENCY, modulatorFrequency)
            props.csound.setControlChannel(AM_AMOUNT, amplitudeModulationAmount)
            props.csound.setControlChannel(AM_FREQUENCY, amplitudeModulationFrequency)
            props.csound.setControlChannel(BIT_REDUCER, bitReducerAmount)
            props.csound.evaluateCode("schedule(1, 0, -1)")
        })

        return () => {
            props.csound.reset()
        }
    }

    React.useEffect(startCsound, [])

    return (
        <div className={classes.DSPPanel}>
            <div className={[classes.Headers, classes.FullRow].join(" ")}>Carrier</div>
            <LabeledHRange
                id="amplitude_"
                label="Amplitude"
                small="Amplitude of the carrier. [0-1]"
                min={0}
                max={1}
                step={0.005}
                onChange={amplitudeChangeHandler}
                value={carrierAmplitude}
                extraClasses={classes.LabeledHRange}
            />
            <LabeledHRange
                id="frequency_"
                label="Frequency"
                small="Nominal frequency of the carrier. [110Hz-3520Hz]"
                min={0}
                max={1}
                step={0.005}
                onChange={carrierFrequencyChangeHandler}
                value={carrierFrequency}
                extraClasses={classes.LabeledHRange}
            />
            <LabeledHRange
                id="panning_"
                label="Panning"
                small="Stereo spatial distribution of the signal. [0-1]"
                min={0}
                max={1}
                step={0.01}
                onChange={panningChangeHandler}
                value={panning}
                extraClasses={[classes.LabeledHRange].join(" ")}
            />
            <div style={{ display: "flex", justifyContent: "space-evenly", width: "100%" }}>
                <NoteButton note={-1} />
                <NoteButton note={8} onMouseEnter={() => noteMouseEnterHandler(349.23)} />
                <NoteButton note={7} onMouseEnter={() => noteMouseEnterHandler(392.0)} />
                <NoteButton note={6} onMouseEnter={() => noteMouseEnterHandler(440.0)} />
                <NoteButton note={4} onMouseEnter={() => noteMouseEnterHandler(523.25)} />
                <NoteButton note={3} onMouseEnter={() => noteMouseEnterHandler(587.33)} />
                <NoteButton note={1} onMouseEnter={() => noteMouseEnterHandler(698.46)} />
            </div>
            <div className={[classes.Headers, classes.FullRow].join(" ")}>
                Frequency Modulator
            </div>
            <LabeledHRange
                id="modulator_amplitude_"
                label="Index"
                small="Amplitude of the modulator signal. [0-20]"
                min={0}
                max={20}
                step={0.01}
                onChange={modulatorAmplitudeChangeHandler}
                value={modulatorAmplitude}
                extraClasses={classes.LabeledHRange}
            />
            <LabeledHRange
                id="modulator_frequency_"
                label="Deviation"
                small="Frequency ratio of the modulator signal. [0-1]"
                min={0}
                max={1}
                step={0.01}
                onChange={modulatorFrequencyChangeHandler}
                value={modulatorFrequency}
                extraClasses={classes.LabeledHRange}
            />
            <div className={[classes.FullRow, classes.FMButtons].join(" ")}>
                <RegularButton click={() => modulatorFrequencyButtonClickHandler(0.25)}>
                    <sup>1</sup>&frasl;<sub>4</sub>
                </RegularButton>
                <RegularButton click={() => modulatorFrequencyButtonClickHandler(1 / 3)}>
                    <sup>1</sup>&frasl;<sub>3</sub>
                </RegularButton>
                <RegularButton click={() => modulatorFrequencyButtonClickHandler(0.5)}>
                    <sup>1</sup>&frasl;<sub>2</sub>
                </RegularButton>
                <RegularButton click={() => modulatorFrequencyButtonClickHandler(2 / 3)}>
                    <sup>2</sup>&frasl;<sub>3</sub>
                </RegularButton>
                <RegularButton click={() => modulatorFrequencyButtonClickHandler(0.75)}>
                    <sup>3</sup>&frasl;<sub>4</sub>
                </RegularButton>
            </div>
            <div className={[classes.Headers, classes.FullRow].join(" ")}>
                Amplitude Modulator
            </div>
            <LabeledHRange
                id="am_amplitude_"
                label="Amount"
                small="Amount of modulation. [0%-100%]"
                min={0}
                max={1}
                step={0.01}
                onChange={amplitudeModulationAmountChangeHandler}
                value={amplitudeModulationAmount}
                extraClasses={classes.LabeledHRange}
            />
            <LabeledHRange
                id="am_frequency_"
                label="Frequency"
                small="AM frequency. [0.2Hz-3520Hz]"
                min={0}
                max={1}
                step={0.01}
                onChange={amplitudeModulationFrequencyChangeHandler}
                value={amplitudeModulationFrequency}
                extraClasses={classes.LabeledHRange}
            />
            <div className={[classes.Headers, classes.FullRow].join(" ")}>Bit Reduction</div>
            <LabeledHRange
                id="bit_reducer_amount_"
                label="Amount"
                small="Bit depth of the audio signal. [0-16]"
                min={0}
                max={1}
                step={0.01}
                onChange={bitReducerChangeHandler}
                value={bitReducerAmount}
                extraClasses={[classes.LabeledHRange, classes.Last].join(" ")}
            />
        </div>
    )
}

export default DSPPanel
